import axios from 'axios';
import { jsPDF } from 'jspdf';
import _report_fontFiles_ko_medium from '@shared/assets/fonts/report_fonts_ttf/NotoSansKR/NotoSansKR-Medium.ttf';
import _report_fontFiles_ko_normal from '@shared/assets/fonts/report_fonts_ttf/NotoSansKR/NotoSansKR-Regular.ttf';
import _report_fontFiles_ko_semibold from '@shared/assets/fonts/report_fonts_ttf/NotoSansKR/NotoSansKR-SemiBold.ttf';
import _report_fontFiles_ko_bold from '@shared/assets/fonts/report_fonts_ttf/NotoSansKR/NotoSansKR-Bold.ttf';

import report_fontFiles_ca_medium from '@shared/assets/fonts/report_fonts_ttf/NotoSansSC/NotoSansSC-Medium.ttf';
import report_fontFiles_ca_normal from '@shared/assets/fonts/report_fonts_ttf/NotoSansSC/NotoSansSC-Regular.ttf';
import report_fontFiles_ca_semibold from '@shared/assets/fonts/report_fonts_ttf/NotoSansSC/NotoSansSC-SemiBold.ttf';
import report_fontFiles_ca_bold from '@shared/assets/fonts/report_fonts_ttf/NotoSansSC/NotoSansSC-Bold.ttf';

// 보고서 mixin 들이 jsPDF addImage 에 URL 로 넘기는 이미지 전부.
// 여기 없는 URL 은 jsPDF 기본 동작(동기 XHR)으로 그대로 받으므로 빠져도 깨지지는 않는다.
import _img_cover from '@shared/assets/img/common/report_cover_ge_plain.png';
import _img_qesg_logo from '@shared/assets/img/common/QuantifiedESG_Logo_v3_Basic.png';
import _img_qesg_logo_white from '@shared/assets/img/QuantifiedESG_Logo_v3_White.png';
import _img_background_texture from '@shared/assets/img/background_texture.png';
import _img_shortcut from '@shared/assets/img/IcBaselineShortcut.png';
import _img_hyundai_logo from '@shared/assets/img/hyundai_logo.png';
import _img_hyundai_logo_blue from '@shared/assets/img/hyundai_logo_blue.png';
import _img_kia_logo from '@shared/assets/img/kia_logo.png';
import _img_ksteel_logo from '@shared/assets/img/ksteel_logo.png';
import _img_ksteel_logo_white from '@shared/assets/img/ksteel_logo_white.png';

const REPORT_IMAGES = [
    _img_cover, _img_qesg_logo, _img_qesg_logo_white, _img_background_texture, _img_shortcut,
    _img_hyundai_logo, _img_hyundai_logo_blue, _img_kia_logo, _img_ksteel_logo, _img_ksteel_logo_white
];

// url -> jsPDF loadFile 이 돌려주는 것과 같은 binary string
const imageCache = new Map();

export default {
    install(Vue) {
        if (!Vue.prototype.$report) {
            Vue.prototype.$report = {};
        }

        Vue.prototype.$report.fonts = {
            'NotoSansKR': {
                bold: _report_fontFiles_ko_bold,
                semibold: _report_fontFiles_ko_semibold,
                medium: _report_fontFiles_ko_medium,
                normal: _report_fontFiles_ko_normal
            },
            'NotoSansSC': {
                bold: report_fontFiles_ca_bold,
                semibold: report_fontFiles_ca_semibold,
                medium: report_fontFiles_ca_medium,
                normal: report_fontFiles_ca_normal
            },
        };

        Vue.prototype.$report.cachedFont = {
            'NotoSansKR': {
                bold: null,
                semibold: null,
                medium: null,
                normal: null
            },
            'NotoSansSC': {
                bold: null,
                semibold: null,
                medium: null,
                normal: null
            },
        };

        // 화면 표시용 진행 상태. files[].total 은 첫 progress 이벤트에서 채워진다.
        const progress = Vue.observable({ active: false, done: false, files: [] });
        Vue.prototype.$report.assetProgress = progress;

        // 미리 받은 이미지는 동기 XHR 없이 캐시에서 돌려준다
        const _original_load_file = jsPDF.API.loadFile;
        jsPDF.API.loadFile = function (url, sync, callback) {
            if (imageCache.has(url)) {
                const _data = imageCache.get(url);
                if (sync === false && typeof callback === 'function') callback(_data);
                return _data;
            }
            return _original_load_file.call(this, url, sync, callback);
        };

        let _loading = null;
        // 폰트 + 이미지를 한 번에 병렬로 받는다. 동시 호출(created/mounted)은 같은 Promise 를 공유한다.
        Vue.prototype.$report.loadFont = () => {
            if (progress.done) return Promise.resolve();
            if (_loading) return _loading;

            const _fonts = [];
            Object.entries(Vue.prototype.$report.fonts).forEach(([_family, _weights]) => {
                Object.entries(_weights).forEach(([_weight, _url]) => {
                    _fonts.push({ kind: 'font', url: _url, family: _family, weight: _weight });
                });
            });
            // url-loader 가 data URI 로 인라인한 작은 이미지는 받을 필요가 없다
            const _images = [...new Set(REPORT_IMAGES)]
                .filter(_url => typeof _url === 'string' && !_url.startsWith('data:'))
                .map(_url => ({ kind: 'image', url: _url }));

            const _targets = [..._fonts, ..._images];
            progress.files = _targets.map(({ kind, url }) => ({ kind, url, loaded: 0, total: null, done: false }));
            progress.active = true;

            _loading = Promise.all(_targets.map(async (_target, _idx) => {
                const _file = progress.files[_idx];
                const _res = await axios.get(_target.url, {
                    responseType: 'arraybuffer',
                    onDownloadProgress: (e) => {
                        _file.loaded = e.loaded;
                        if (e.lengthComputable) _file.total = e.total;
                    }
                });
                const _bytes = new Uint8Array(_res.data);
                if (_target.kind === 'font') {
                    // jsPDF 는 TTF 시그니처(00 01 00 00)로 시작하는 binary string 이면 atob 없이 쓴다 — base64 왕복 불필요
                    const _font = _target.url.endsWith('.gz.bin') ? await gunzip(_bytes) : _bytes;
                    Vue.prototype.$report.cachedFont[_target.family][_target.weight] = bytesToBinaryString(_font);
                } else {
                    imageCache.set(_target.url, bytesToBinaryString(_bytes));
                }
                // 전송 바이트(압축 포함)와 단위를 맞추기 위해 total 이 있으면 total 로 마감
                _file.total = _file.total || _file.loaded || _bytes.byteLength;
                _file.loaded = _file.total;
                _file.done = true;
            })).then(() => {
                progress.done = true;
                progress.active = false;
            }).catch((_err) => {
                // 실패하면 다음 호출에서 다시 받도록 초기화
                _loading = null;
                progress.active = false;
                throw _err;
            });

            return _loading;
        };
    }
};

// 빌드 시 gzip 한 폰트(apps/supply/gzip-loader.js)를 푼다. DecompressionStream 미지원 브라우저는 fflate 로.
const gunzip = async (bytes) => {
    if (typeof window.DecompressionStream === 'function') {
        const _stream = new Blob([bytes]).stream().pipeThrough(new window.DecompressionStream('gzip'));
        return new Uint8Array(await new Response(_stream).arrayBuffer());
    }
    const { gunzipSync } = await import('fflate');
    return gunzipSync(bytes);
};

// String.fromCharCode 를 청크 단위로 호출 (바이트당 문자열 연결보다 수십 배 빠름)
const bytesToBinaryString = (bytes) => {
    const _chunk = 0x8000;
    let binary = '';
    for (let i = 0; i < bytes.length; i += _chunk) {
        binary += String.fromCharCode.apply(null, bytes.subarray(i, i + _chunk));
    }
    return binary;
};
